import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import {
  BuildConfiguration,
  BuildConfigurationCreate,
  BuildConfigurationUpdate,
  BuildStrategy,
  DeploymentBySource
} from '../../types/api';
import { getApiErrorMessage } from '../../constants/messages';
import { Modal, Button } from '../ui';
import Logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';

interface BuildConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  repositoryId: number;
  repositoryName: string;
  currentConfig?: BuildConfiguration | null;
  onConfigured: () => void;
}

const BuildConfigModal: React.FC<BuildConfigModalProps> = ({
  isOpen,
  onClose,
  repositoryId,
  repositoryName,
  currentConfig,
  onConfigured
}) => {
  const { t } = (useTranslation as any)('builds');
  // Form state
  const [name, setName] = useState('default');
  const [buildStrategy, setBuildStrategy] = useState<BuildStrategy>('dockerfile');
  const [sourceDirectory, setSourceDirectory] = useState('.');
  const [dockerfilePath, setDockerfilePath] = useState('./Dockerfile');
  const [builderImage, setBuilderImage] = useState('');
  const [imageTagPattern, setImageTagPattern] = useState('latest');
  const [autoBuildEnabled, setAutoBuildEnabled] = useState(false);
  // L'AUTO-DEPLOIEMENT N'EST PAS UN CHAMP DE CETTE CONFIGURATION. Il vit sur le
  // DEPLOIEMENT (`auto_deploy_enabled`, V059), parce que plusieurs deploiements, dans
  // des environnements differents, peuvent suivre le meme couple (depot, branche) --
  // ce qu'un champ unique ici plafonnait a un seul. Cet ecran n'est qu'une PORTE
  // D'ENTREE sur ce stockage : il ecrit par `PATCH /deployment/{id}/auto-deploy`,
  // comme la fiche du deploiement. Deux stockages divergeraient.
  const [cibles, setCibles] = useState<DeploymentBySource[] | null>(null);
  const [cibleEnCours, setCibleEnCours] = useState<string | null>(null);
  const [triggerOnPush, setTriggerOnPush] = useState(false);
  const [triggerOnTag, setTriggerOnTag] = useState(false);
  const [triggerOnPr, setTriggerOnPr] = useState(false);
  const [branchFilter, setBranchFilter] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const isEditMode = !!currentConfig;

  useEffect(() => {
    if (currentConfig) {
      setName(currentConfig.name);
      setBuildStrategy(currentConfig.build_strategy);
      setSourceDirectory(currentConfig.source_directory);
      setDockerfilePath(currentConfig.dockerfile_path);
      setBuilderImage(currentConfig.builder_image || '');
      setImageTagPattern(currentConfig.image_tag_pattern);
      setAutoBuildEnabled(currentConfig.auto_build_enabled);
      setTriggerOnPush(currentConfig.trigger_on_push);
      setTriggerOnTag(currentConfig.trigger_on_tag);
      setTriggerOnPr(currentConfig.trigger_on_pr);
      setBranchFilter(currentConfig.branch_filter || '');
      setPathFilter(currentConfig.path_filter || '');
    } else {
      // Reset to defaults
      setName('default');
      setBuildStrategy('dockerfile');
      setSourceDirectory('.');
      setDockerfilePath('./Dockerfile');
      setBuilderImage('');
      setImageTagPattern('latest');
      setAutoBuildEnabled(false);
      setTriggerOnPush(false);
      setTriggerOnTag(false);
      setTriggerOnPr(false);
      setBranchFilter('');
      setPathFilter('');
    }
  }, [currentConfig, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (autoBuildEnabled && !isImmutablePattern(imageTagPattern)) {
      toast.error(t('buildConfigModal.tagPatternAutoBuildError') as string);
      return;
    }

    try {
      setLoading(true);

      if (isEditMode && currentConfig) {
        const updateData: BuildConfigurationUpdate = {
          name,
          build_strategy: buildStrategy,
          source_directory: sourceDirectory,
          dockerfile_path: dockerfilePath,
          builder_image: builderImage || null,
          image_tag_pattern: imageTagPattern,
          auto_build_enabled: autoBuildEnabled,
          trigger_on_push: triggerOnPush,
          trigger_on_tag: triggerOnTag,
          trigger_on_pr: triggerOnPr,
          branch_filter: branchFilter || null,
          path_filter: pathFilter || null
        };
        await apiService.updateBuildConfiguration(repositoryId, currentConfig.id, updateData);
        toast.success(t('buildConfigModal.updateSuccess') as string);
      } else {
        const createData: BuildConfigurationCreate = {
          name,
          build_strategy: buildStrategy,
          source_directory: sourceDirectory,
          dockerfile_path: dockerfilePath,
          builder_image: builderImage || null,
          image_tag_pattern: imageTagPattern,
          auto_build_enabled: autoBuildEnabled,
          trigger_on_push: triggerOnPush,
          trigger_on_tag: triggerOnTag,
          trigger_on_pr: triggerOnPr,
          branch_filter: branchFilter || null,
          path_filter: pathFilter || null
        };
        await apiService.createBuildConfiguration(repositoryId, createData);
        toast.success(t('buildConfigModal.createSuccess') as string);
      }

      onConfigured();
      onClose();
    } catch (error: unknown) {
      Logger.error('Error saving build config:', error);
      toast.error(getApiErrorMessage(error) || t('buildConfigModal.saveError') as string);
    } finally {
      setLoading(false);
    }
  };

  // L'auto-build exige un tag immuable ({sha} + {date}) : sans lui, un nouveau
  // build écrase le tag dans le registry sans redéploiement des pods.
  const isImmutablePattern = (p: string) => p.includes('{sha}') && p.includes('{date}');

  const tagPatternPresets = [
    { label: 'Branche + Date + SHA (ex: main-20260208-a1b2c3d)', value: '{branch}-{date}-{sha}' },
    { label: 'Date + SHA (ex: 20260208-a1b2c3d)', value: '{date}-{sha}' },
    { label: 'Config + Branche + Date + SHA (ex: frontend-main-20260208-a1b2c3d)', value: '{name}-{branch}-{date}-{sha}' },
    { label: 'latest', value: 'latest' },
    { label: 'Branche (ex: main)', value: '{branch}' },
    { label: 'SHA court (ex: a1b2c3d)', value: '{sha}' },
    { label: 'Branche + SHA', value: '{branch}-{sha}' },
    { label: 'Date (ex: 20260208)', value: '{date}' },
    { label: 'Config + latest (ex: frontend-latest)', value: '{name}-latest' },
    { label: 'Config + Branche (ex: frontend-main)', value: '{name}-{branch}' },
    { label: 'Config + Branche + SHA (ex: frontend-main-a1b2c3d)', value: '{name}-{branch}-{sha}' }
  ];

  const visibleTagPatternPresets = autoBuildEnabled
    ? tagPatternPresets.filter((p) => isImmutablePattern(p.value))
    : tagPatternPresets;

  // Si l'auto-build vient d'être activé avec un pattern mutable, basculer sur un preset conforme
  useEffect(() => {
    if (autoBuildEnabled && !isImmutablePattern(imageTagPattern)) {
      setImageTagPattern('{branch}-{date}-{sha}');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBuildEnabled]);

  // Les deploiements du projet de ce depot, avec leur etat de suivi.
  useEffect(() => {
    if (!isOpen) { setCibles(null); return; }
    let vivant = true;
    apiService.getDeploymentsBySource(repositoryId)
      .then((r) => { if (vivant) setCibles(r.deploiements); })
      .catch((error) => {
        Logger.error('Chargement des deploiements du depot:', error);
        // `null` dit « on ne sait pas » : l'ecran ne doit pas annoncer qu'il n'y a
        // aucun deploiement quand c'est la lecture qui a echoue.
        if (vivant) setCibles(null);
      });
    return () => { vivant = false; };
  }, [isOpen, repositoryId]);

  /** Bascule le suivi d'un deploiement. Une seule ecriture, par l'endpoint du
   *  deploiement : cet ecran ne stocke rien de son cote. */
  const basculerSuivi = async (d: DeploymentBySource, actif: boolean) => {
    setCibleEnCours(d.deployment_id);
    try {
      const etat = await apiService.setAutoDeploy(d.deployment_id, {
        enabled: actif,
        // La source n'est transmise QUE pour activer : desactiver ne doit pas
        // reecrire le depot ni la branche du deploiement.
        ...(actif ? { connected_repository_id: repositoryId } : {}),
      });
      setCibles((avant) => (avant || []).map((x) =>
        x.deployment_id === d.deployment_id
          ? { ...x, auto_deploy_enabled: etat.auto_deploy_enabled,
              connected_repository_id: etat.connected_repository_id,
              git_branch: etat.git_branch,
              suit_ce_depot: etat.connected_repository_id === repositoryId }
          : x));
      toast.success(actif
        ? (t('deploy.autoDeploySaved') as string)
        : (t('deploy.autoDeployOff') as string));
    } catch (error) {
      Logger.error('Bascule de l auto-deploiement:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setCibleEnCours(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? `Modifier "${currentConfig?.name}"` : 'Nouvelle configuration de build'}
      size="2xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-6">

          {/* Repository Info */}
          <div className="bg-surface-3 rounded-lg p-3">
            <p className="text-sm text-text-secondary">
              Repository: <span className="font-medium text-text-primary">{repositoryName}</span>
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.configName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex: frontend, backend, api"
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
              required
            />
            <p className="text-xs text-text-tertiary mt-1">
              Pour les monorepos, créez une config par sous-projet (ex: frontend, backend)
            </p>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-text-primary">
                {t('buildConfigModal.enableAutoBuild')}
              </label>
              <p className="text-xs text-text-tertiary mt-1">
                Le build sera déclenché automatiquement lors des webhooks
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoBuildEnabled}
                onChange={(e) => setAutoBuildEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-4 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
            </label>
          </div>

          {/* Build Strategy */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.buildStrategy')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="buildStrategy"
                  value="dockerfile"
                  checked={buildStrategy === 'dockerfile'}
                  onChange={() => setBuildStrategy('dockerfile')}
                  className="w-4 h-4 text-accent-600 bg-surface-3 border-border-1 focus:ring-accent-500"
                />
                <span className="text-sm text-text-primary">Dockerfile</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="buildStrategy"
                  value="buildpack"
                  checked={buildStrategy === 'buildpack'}
                  onChange={() => setBuildStrategy('buildpack')}
                  className="w-4 h-4 text-accent-600 bg-surface-3 border-border-1 focus:ring-accent-500"
                />
                <span className="text-sm text-text-primary">Buildpacks (Cloud Native)</span>
              </label>
            </div>
          </div>

          {/* Source Directory (for monorepos) */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.sourceDirectory')}
            </label>
            <input
              type="text"
              value={sourceDirectory}
              onChange={(e) => setSourceDirectory(e.target.value)}
              placeholder="."
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
            <p className="text-xs text-text-tertiary mt-1">
              Racine du code source relative au dépôt (ex: backend, frontend, apps/api). Laissez « . » pour la racine.
            </p>
          </div>

          {/* Dockerfile Path (only for dockerfile strategy) */}
          {buildStrategy === 'dockerfile' && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {t('buildConfigModal.dockerfilePath')}
              </label>
              <input
                type="text"
                value={dockerfilePath}
                onChange={(e) => setDockerfilePath(e.target.value)}
                placeholder="./Dockerfile"
                className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
              <p className="text-xs text-text-tertiary mt-1">
                Relatif au dossier source ci-dessus. Si votre Dockerfile est à la racine du dossier source, laissez « ./Dockerfile ».
              </p>
            </div>
          )}

          {/* Builder Image (only for buildpack strategy) */}
          {buildStrategy === 'buildpack' && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {t('buildConfigModal.builderImage')}
              </label>
              <input
                type="text"
                value={builderImage}
                onChange={(e) => setBuilderImage(e.target.value)}
                placeholder="paketobuildpacks/builder-jammy-base"
                className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>
          )}

          {/* Image Tag Pattern */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.tagPattern')}
            </label>
            <select
              value={imageTagPattern}
              onChange={(e) => setImageTagPattern(e.target.value)}
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              {visibleTagPatternPresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
            {autoBuildEnabled && (
              <p className="mt-1 text-xs text-yellow-400">
                {t('buildConfigModal.tagPatternAutoBuildHint')}
              </p>
            )}
            <p className="mt-1 text-xs text-text-secondary">
              Pour les monorepos, utilisez les patterns avec {'{name}'} pour différencier les images (résout vers le nom de la config)
            </p>
          </div>

          {/* Trigger Events */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.triggerOn')}
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={triggerOnPush}
                  onChange={(e) => setTriggerOnPush(e.target.checked)}
                  className="w-4 h-4 text-accent-600 bg-surface-3 rounded border-border-1 focus:ring-accent-500"
                />
                <span className="text-sm text-text-primary">Push (commits)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={triggerOnTag}
                  onChange={(e) => setTriggerOnTag(e.target.checked)}
                  className="w-4 h-4 text-accent-600 bg-surface-3 rounded border-border-1 focus:ring-accent-500"
                />
                <span className="text-sm text-text-primary">Tags (releases)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={triggerOnPr}
                  onChange={(e) => setTriggerOnPr(e.target.checked)}
                  className="w-4 h-4 text-accent-600 bg-surface-3 rounded border-border-1 focus:ring-accent-500"
                />
                <span className="text-sm text-text-primary">Pull Requests</span>
              </label>
            </div>
          </div>

          {/* Branch Filter */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.branchFilter')}
            </label>
            <input
              type="text"
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              placeholder="main,develop ou feature/*"
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
            <p className="text-xs text-text-tertiary mt-1">
              Laissez vide pour toutes les branches. Utilisez des virgules pour plusieurs branches ou * pour les wildcards.
            </p>
          </div>

          {/* Path Filter */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('buildConfigModal.pathFilter')}
            </label>
            <input
              type="text"
              value={pathFilter}
              onChange={(e) => setPathFilter(e.target.value)}
              placeholder="frontend/**,shared/**"
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
            <p className="text-xs text-text-tertiary mt-1">
              Le build ne se déclenchera que si des fichiers modifiés correspondent à ces patterns.
            </p>
          </div>

          {/* Auto-deploiement : une PORTE sur l'etat du deploiement, pas un champ
              de cette configuration. Voir le commentaire sur `cibles` plus haut. */}
          <div className="border-t border-border-1 pt-4">
            <h4 className="text-sm font-semibold text-text-primary">
              {t('deploy.autoDeploy') as string}
            </h4>
            <p className="text-xs text-text-tertiary mt-1">
              {t('deploy.autoDeployHint') as string}
            </p>

            {cibles === null && (
              <p className="text-xs text-text-tertiary mt-3">
                {t('deploy.loading') as string}
              </p>
            )}

            {cibles !== null && cibles.length === 0 && (
              <p className="text-xs text-text-tertiary mt-3">
                {t('deploy.autoDeployNone') as string}
              </p>
            )}

            {cibles !== null && cibles.length > 0 && (
              <div className="mt-3 space-y-2">
                <span className="block text-xs font-medium text-text-secondary">
                  {t('deploy.autoDeployTarget') as string}
                </span>
                {cibles.map((d) => (
                  <label
                    key={d.deployment_id}
                    className="flex items-start gap-3 rounded-lg border border-border-1 p-3 cursor-pointer hover:bg-surface-3"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={d.auto_deploy_enabled && d.suit_ce_depot}
                      disabled={cibleEnCours === d.deployment_id}
                      onChange={(e) => basculerSuivi(d, e.target.checked)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-text-primary">{d.nom}</span>
                      <span className="block text-xs text-text-tertiary mt-0.5">
                        {d.connected_repository_id == null
                          ? (t('deploy.followsOther') as string)
                          : d.suit_ce_depot
                            ? (t('deploy.followsThisRepo') as string)
                            : (t('deploy.followsOther') as string)}
                        {d.git_branch && ` · ${d.git_branch}`}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/12 border border-blue-500/20 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">
              {t('buildConfigModal.infoTitle')}
            </h4>
            <ul className="text-xs text-blue-400 space-y-1 list-disc list-inside">
              <li>Les webhooks déclenchent automatiquement les builds selon cette configuration</li>
              <li>Pour les monorepos, créez une configuration par sous-projet</li>
              <li>Utilisez les filtres de path pour éviter les builds inutiles</li>
              <li>Vous pouvez aussi déclencher des builds manuellement</li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-4 border-t border-border-1 sticky bottom-0 bg-surface-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {isEditMode ? t('buildConfigModal.update') as string : t('common:create') as string}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BuildConfigModal;
